<script>
	import { onMount } from 'svelte';
	import katex from 'katex';
	// KaTeX CSS is loaded via CDN in the HTML template to avoid font file issues

	let exercises = [];
	let loading = true;
	let error = '';
	
	// Track selected answers for each exercise: { exerciseId: optionId }
	let selectedAnswers = {};

	// Difficulty labels in Lithuanian
	const difficultyLabels = {
		easy: 'Lengvas',
		medium: 'Vidutinis',
		hard: 'Sunkus'
	};

	// Difficulty colors
	const difficultyColors = {
		easy: '#fef3c7', // light yellow
		medium: '#fde68a', // medium yellow
		hard: '#fbbf24' // darker yellow
	};
	
	// Handle option selection
	function selectOption(exerciseId, optionId, isCorrect) {
		// Ensure IDs are numbers for consistent comparison
		const exerciseIdNum = Number(exerciseId);
		const optionIdNum = Number(optionId);
		
		// Create a new object to trigger reactivity
		selectedAnswers = {
			...selectedAnswers,
			[exerciseIdNum]: optionIdNum
		};
		console.log('Selected answer:', exerciseIdNum, optionIdNum, isCorrect);
		console.log('Selected answers:', selectedAnswers);
	}
	
	// Check if an option is selected
	function isSelected(exerciseId, optionId) {
		return selectedAnswers[exerciseId] === optionId;
	}
	
	// Get the correct option ID for an exercise
	function getCorrectOptionId(exercise) {
		const correctOption = exercise.options.find(opt => opt.is_correct);
		return correctOption ? Number(correctOption.id) : null;
	}
	
	// Determine the state of an option button
	function getOptionState(exercise, optionId) {
		// Explicitly reference selectedAnswers to ensure reactivity
		const selectedId = selectedAnswers[exercise.id];
		if (!selectedId) return 'default'; // No selection yet
		
		if (optionId === selectedId) {
			// This is the selected option
			const correctId = getCorrectOptionId(exercise);
			const isCorrect = optionId === correctId;
			console.log(`Option ${optionId} selected, correct ID: ${correctId}, isCorrect: ${isCorrect}`);
			return isCorrect ? 'correct' : 'incorrect';
		}
		
		// If another option was selected, show this one as default
		return 'default';
	}
	
	// Helper function to check if option is correct (reactive)
	function isOptionCorrect(exercise, optionId) {
		const selectedId = selectedAnswers[exercise.id];
		if (!selectedId || optionId !== selectedId) return false;
		const correctId = getCorrectOptionId(exercise);
		return optionId === correctId;
	}
	
	// Helper function to check if option is incorrect (reactive)
	function isOptionIncorrect(exercise, optionId) {
		const selectedId = selectedAnswers[exercise.id];
		if (!selectedId || optionId !== selectedId) return false;
		const correctId = getCorrectOptionId(exercise);
		return optionId !== correctId;
	}

	// Function to render text with LaTeX
	function renderWithLatex(text) {
		if (!text) return [{ type: 'text', content: '' }];
		
		// Regex to match LaTeX expressions: \(...\) for inline, \[...\] for display, or $...$
		// Updated to handle escaped delimiters and various formats
		const latexRegex = /(\\?\[.*?\\?\]|\\?\(.*?\\?\)|\$[^$]+\$)/g;
		const parts = [];
		let lastIndex = 0;
		let match;
		
		while ((match = latexRegex.exec(text)) !== null) {
			// Add text before the match
			if (match.index > lastIndex) {
				const textPart = text.substring(lastIndex, match.index);
				if (textPart) {
					parts.push({ type: 'text', content: textPart });
				}
			}
			
			// Extract LaTeX content (remove delimiters)
			let latexContent = match[1];
			// Remove \( \) or \[ \] or $ delimiters
			if (latexContent.startsWith('\\[') && latexContent.endsWith('\\]')) {
				latexContent = latexContent.slice(2, -2);
			} else if (latexContent.startsWith('\\(') && latexContent.endsWith('\\)')) {
				latexContent = latexContent.slice(2, -2);
			} else if (latexContent.startsWith('[') && latexContent.endsWith(']')) {
				latexContent = latexContent.slice(1, -1);
			} else if (latexContent.startsWith('(') && latexContent.endsWith(')')) {
				latexContent = latexContent.slice(1, -1);
			} else if (latexContent.startsWith('$') && latexContent.endsWith('$')) {
				latexContent = latexContent.slice(1, -1);
			}
			
			// Add rendered math
			try {
				const rendered = katex.renderToString(latexContent.trim(), {
					throwOnError: false,
					displayMode: false
				});
				parts.push({ type: 'math', content: rendered });
			} catch (e) {
				// If rendering fails, show original
				parts.push({ type: 'text', content: match[0] });
			}
			
			lastIndex = match.index + match[0].length;
		}
		
		// Add remaining text
		if (lastIndex < text.length) {
			const remainingText = text.substring(lastIndex);
			if (remainingText) {
				parts.push({ type: 'text', content: remainingText });
			}
		}
		
		// If no LaTeX found, return original text
		if (parts.length === 0) {
			return [{ type: 'text', content: text }];
		}
		
		return parts;
	}

	// Fetch exercises from API
	async function fetchExercises() {
		loading = true;
		error = '';
		
		try {
			const token = localStorage.getItem('jwtToken');
			if (!token) {
				error = 'Reikalinga autentifikacija. Prisijunkite prie sistemos.';
				loading = false;
				return;
			}
			
			const response = await fetch('/studentplayground/api/exercise/mcq/', {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});
			
			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				error = errorData.error || 'Nepavyko gauti užduočių duomenų';
				loading = false;
				return;
			}
			
			const data = await response.json();
			if (data.status === 'success') {
				exercises = data.exercises || [];
			} else {
				error = data.error || 'Netikėta klaida';
			}
		} catch (err) {
			console.error('Error fetching exercises:', err);
			error = 'Tinklo klaida. Bandykite dar kartą.';
		} finally {
			loading = false;
		}
	}

	onMount(() => {
		fetchExercises();
	});
	
	// Debug: Log when selectedAnswers changes
	$: console.log('selectedAnswers updated:', selectedAnswers);
	
	// Force reactivity by creating a reactive statement that references selectedAnswers
	// This ensures Svelte tracks changes to selectedAnswers properly
	$: selectedAnswers, exercises;
</script>

<div class="exercises-container">
	{#if loading}
		<div class="loading">Kraunama...</div>
	{:else if error}
		<div class="error">{error}</div>
	{:else if exercises.length === 0}
		<div class="empty">Nėra prieinamų užduočių.</div>
	{:else}
		<div class="exercises-list">
			{#each exercises as exercise}
				<div class="exercise-card">
					<!-- Difficulty Badge -->
					<div class="exercise-header">
						<div 
							class="difficulty-badge" 
							style="background-color: {difficultyColors[exercise.difficulty] || difficultyColors.easy}"
						>
							<span class="difficulty-text">{difficultyLabels[exercise.difficulty] || exercise.difficulty}</span>
							<svg class="star-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
							</svg>
							<svg class="bookmark-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
							</svg>
						</div>
					</div>

					<!-- Question Text -->
					<div class="question-text">
						{#each renderWithLatex(exercise.question_text) as part}
							{#if part.type === 'math'}
								{@html part.content}
							{:else}
								{part.content}
							{/if}
						{/each}
					</div>

					<!-- Options -->
					<div class="options-container">
						{#each exercise.options as option, index (option.id)}
							<button 
								class="option-button"
								class:correct={selectedAnswers[Number(exercise.id)] === Number(option.id) && Number(option.id) === getCorrectOptionId(exercise)}
								class:incorrect={selectedAnswers[Number(exercise.id)] === Number(option.id) && Number(option.id) !== getCorrectOptionId(exercise)}
								type="button"
								on:click={() => selectOption(exercise.id, option.id, option.is_correct)}
							>
								<span class="option-label">{String.fromCharCode(65 + index)}.</span>
								<span class="option-text">
									{#each renderWithLatex(option.text) as part}
										{#if part.type === 'math'}
											{@html part.content}
										{:else}
											{part.content}
										{/if}
									{/each}
								</span>
							</button>
						{/each}
					</div>
				</div>
			{/each}
		</div>
	{/if}
</div>

<style>
	.exercises-container {
		width: 100%;
		padding: 24px;
	}

	.loading,
	.error,
	.empty {
		text-align: center;
		padding: 48px;
		color: #4b5563;
		font-size: 16px;
	}

	.error {
		color: #ef4444;
	}

	.exercises-list {
		display: flex;
		flex-direction: column;
		gap: 32px;
		max-width: 800px;
		margin: 0 auto;
	}

	.exercise-card {
		background-color: white;
		border-radius: 12px;
		padding: 24px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	}

	.exercise-header {
		margin-bottom: 16px;
	}

	.difficulty-badge {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 12px;
		border-radius: 6px;
		font-size: 14px;
		font-weight: 500;
	}

	.difficulty-text {
		color: #111827;
	}

	.star-icon,
	.bookmark-icon {
		color: #9ca3af;
		width: 16px;
		height: 16px;
		flex-shrink: 0;
	}

	.question-text {
		color: #1e3a8a;
		font-size: 16px;
		line-height: 1.6;
		margin-bottom: 24px;
		font-weight: 500;
	}

	.question-text :global(.katex) {
		font-size: 1em;
	}

	.options-container {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.option-button {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 12px 16px;
		background-color: #e0f2fe;
		border: 2px solid transparent;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.2s ease;
		text-align: left;
		font-family: inherit;
		width: 100%;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	}

	.option-button:hover:not(.correct):not(.incorrect) {
		background-color: #bae6fd;
		transform: translateY(-1px);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}

	.option-button:active:not(.correct):not(.incorrect) {
		transform: translateY(0);
	}

	/* Correct answer styling */
	.option-button.correct {
		background-color: #d1fae5;
		border-color: #10b981;
		box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
	}

	.option-button.correct .option-label {
		color: #10b981;
		font-weight: 700;
	}

	.option-button.correct .option-text {
		color: #065f46;
		font-weight: 500;
	}

	/* Incorrect answer styling */
	.option-button.incorrect {
		background-color: #fee2e2;
		border-color: #ef4444;
		box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
	}

	.option-button.incorrect .option-label {
		color: #ef4444;
		font-weight: 700;
	}

	.option-button.incorrect .option-text {
		color: #991b1b;
		font-weight: 500;
	}

	.option-label {
		font-weight: 600;
		color: #1e40af;
		flex-shrink: 0;
		min-width: 24px;
	}

	.option-text {
		color: #111827;
		font-size: 15px;
		line-height: 1.5;
		flex: 1;
	}

	.option-text :global(.katex) {
		font-size: 1em;
	}

	/* Responsive Design */
	@media (max-width: 768px) {
		.exercises-container {
			padding: 16px;
		}

		.exercise-card {
			padding: 20px;
		}

		.question-text {
			font-size: 15px;
		}

		.option-text {
			font-size: 14px;
		}
	}
</style>

